import { Component, Pipe, PipeTransform, computed, inject, input, output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { CapitalizePipe } from '../contest-sheets/contest-sheets.pipes';
import { GoalNamePipe } from '../contest-sheets/ft-goals-panel.component';
import { IncentiveNamePipe } from '../contest-sheets/ft-finalist-incentives.component';

/**
 * `$sce.trustAsHtml` equivalent — the campaignGoals read-view binds `goalName`/
 * `incentiveName` via `ng-bind-html` (raw, unsanitised HTML), so the native read-view
 * chains the value through this before `[innerHTML]`.
 */
@Pipe({ name: 'safeHtml' })
export class SafeHtmlPipe implements PipeTransform {
    private readonly sanitizer = inject(DomSanitizer);
    transform(value: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
}

/**
 * Native equivalent of the AngularJS `filter:{type:X, isActive:1}` / the directive's
 * `advocateGoalsFilter` (type ∈ {3,4,6} && isActive) used in the campaignGoals read-view.
 * `types` accepts a single type or an array; matching is loose (`==`), which equals the
 * AngularJS substring comparator for the single-digit type/isActive values on real data.
 *
 * IMPURE: the goals modal's deferred save mutates `campaign.goals.item` IN PLACE (push /
 * isActive flips), so a pure pipe keeps serving the stale filter result and a
 * deferred-added goal never appears in the read-view. AngularJS filters re-ran every
 * digest — `pure: false` restores that semantic.
 */
@Pipe({ name: 'campaignGoalFilter', pure: false })
export class CampaignGoalFilterPipe implements PipeTransform {
    transform(goals: any, types: number | number[], isActive: number = 1): any[] {
        const arr = Array.isArray(goals) ? goals : [];
        const typeList = Array.isArray(types) ? types : [types];
        // eslint-disable-next-line eqeqeq
        return arr.filter((g: any) => g && typeList.some((t) => g.type == t) && g.isActive == isActive);
    }
}

/**
 * Native read-view for the AngularJS `ftCampaignGoals` directive's goal panels. One component
 * for all 4 layer variants (pre-stay / post-stay / group-advocacy / photo-advocacy), `*ngIf` per
 * `layer`. Each panel filters `campaign.goals.item`, renders each goal's name + incentive as
 * trusted HTML, and emits `(editGoals)` on click. Distinct from the account/common
 * `CampaignGoalsComponent` — hence the `-ng` selector.
 */
@Component({
    selector: 'ft-campaign-goals-ng',
    imports: [TranslateModule, CapitalizePipe, GoalNamePipe, IncentiveNamePipe, SafeHtmlPipe, CampaignGoalFilterPipe],
    templateUrl: './ft-campaign-goals.component.html'
})
export class FtCampaignGoalsComponent {
    readonly campaign = input<any>();
    readonly propertyIncentives = input<any>();
    readonly culture = input<any>();
    readonly layer = input<any>();
    /** v1 `disabled` attr → readOnly (group-advocacy applies `.read-only` + blocks editGoals). */
    readonly disabled = input(false);
    /** Emitted on panel click — the host sets types/incentiveTag + opens editCampaignGoalsModal. */
    readonly editGoals = output<{ types: number[]; incentiveTag: number }>();

    protected readonly readOnly = computed<boolean>(() => {
        const disabled = this.disabled();
        return disabled === true || (disabled as any) === '' || (disabled as any) === 'true';
    });

    protected onEditGoals(types: number[], incentiveTag: number): void {
        if (!this.readOnly()) {
            this.editGoals.emit({ types, incentiveTag });
        }
    }
}
